// src/services/tablesApi.js

// Central redirector that proxies to tenant servers
const REDIR_BASE = "https://mydata.tablemate.net/redir.php?url=";

const DBG = (...args) => console.log("[tablesApi]", ...args);

// ------------------------------------------
// Build full endpoint URLs per tenant
// ------------------------------------------

/**
 * Build an upstream URL on the tenant server and wrap it in the redirector.
 * @param {string} tenantServerUrl e.g. "https://wazzzasroom.tablemate.net"
 * @param {string} path e.g. "/tablemate/endpoints/tables/fetchTables.php?r=admin"
 */
function buildEndpoint(tenantServerUrl, path) {
  if (!tenantServerUrl) {
    throw new Error("Missing tenantServerUrl for tablesApi");
  }
  console.log("BUILDING: "+JSON.stringify(tenantServerUrl))
  const base = tenantServerUrl.replace(/\/$/, ""); // trim trailing slash
  const upstream = `${base}${path}`;
  return `${REDIR_BASE}${encodeURIComponent(upstream)}`;
}

// Convenience helpers for specific endpoints
function tablesEndpoint(tenantServerUrl) {
  return buildEndpoint(
    tenantServerUrl,
    "/tablemate/endpoints/tables/fetchTables.php?r=admin"
  );
}
function editTableEndpoint(tenantServerUrl) {
  return buildEndpoint(
    tenantServerUrl,
    "/tablemate/endpoints/tables/editTableDetailsBESA.php"
  );
}
function addTableEndpoint(tenantServerUrl) {
  return buildEndpoint(
    tenantServerUrl,
    "/tablemate/endpoints/tables/addTableBESA.php"
  );
}
function deleteTableEndpoint(tenantServerUrl) {
  return buildEndpoint(
    tenantServerUrl,
    "/tablemate/endpoints/tables/deleteTableBESA.php"
  );
}
function openTabEndpoint(tenantServerUrl) {
  return buildEndpoint(
    tenantServerUrl,
    "/tablemate/endpoints/tables/openTableBESA.php"
  );
}
function closeTabEndpoint(tenantServerUrl) {
  return buildEndpoint(
    tenantServerUrl,
    "/tablemate/endpoints/bill/closeTabBESA.php"
  );
}
function forceCloseEndpoint(tenantServerUrl) {
  return buildEndpoint(
    tenantServerUrl,
    "/tablemate/endpoints/tables/closetableForceBESA.php"
  );
}
function fetchEmployeesEndpoint(tenantServerUrl) {
  return buildEndpoint(
    tenantServerUrl,
    "/tablemate/endpoints/employees/fetchAllEmployees.php"
  );
}

// ------------------------------
// Helpers
// ------------------------------
const delay = (ms = 150) => new Promise((r) => setTimeout(r, ms));
const uid = (p = "id") => `${p}_${Math.random().toString(36).slice(2, 9)}`;

function toBool(v) {
  const s = typeof v === "string" ? v.trim().toLowerCase() : v;
  return !(s === 0 || s === "0" || s === false || s === "false" || s == null);
}

function boolTo10(b) {
  return b ? "1" : "0";
}

// generic POST w/ JSON then x-www-form-urlencoded fallback
async function postWithRetry(url, payload) {
  DBG("POST JSON →", url, payload);

  // 1) JSON
  let r = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  DBG("POST JSON response", r.status, r.statusText);

  // 2) fallback to form-encoded
  if (!r.ok) {
    const form = new URLSearchParams();
    Object.entries(payload).forEach(([k, v]) => form.append(k, v ?? ""));
    DBG("POST FORM →", url, Object.fromEntries(form));
    r = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: form.toString(),
    });
    DBG("POST FORM response", r.status, r.statusText);
  }

  const text = await r.text();
  DBG("POST raw body", text?.slice(0, 500)); // avoid spamming
  if (!r.ok)
    throw new Error(
      `HTTP ${r.status} ${r.statusText} ${text || ""}`.trim()
    );

  try {
    const json = JSON.parse(text);
    DBG("POST parsed JSON", json);
    return json;
  } catch {
    DBG("POST non-JSON OK response returned as text");
    return true;
  }
}

async function parseMaybeJSON(resp) {
  const text = await resp.text();
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function postForm(url, dataObj) {
  const body = new URLSearchParams();
  Object.entries(dataObj || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    body.append(k, String(v));
  });

  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
    },
    body,
  });

  const payload = await parseMaybeJSON(resp);

  if (!resp.ok) {
    const msg =
      typeof payload === "object" && payload?.message
        ? payload.message
        : `HTTP ${resp.status} Unhandled error`;
    const err = new Error(msg);
    err.httpStatus = resp.status;
    err.payload = payload;
    throw err;
  }
  return payload;
}

async function getJSON(url) {
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  const text = await res.text();

  if (!res.ok) {
    throw new Error(
      `HTTP ${res.status} ${res.statusText} ${text || ""}`.trim()
    );
  }

  try {
    return JSON.parse(text);
  } catch (e) {
    console.warn(
      "[tablesApi] getJSON: non-JSON response, returning empty array. Raw:",
      text?.slice(0, 300)
    );
    return [];
  }
}

// ------------------------------
// Remote fetch helpers
// ------------------------------

async function fetchRemoteTables(tenantServerUrl) {
  const url = tablesEndpoint(tenantServerUrl);
  DBG("fetchRemoteTables() →", url);

  const response = await fetch(url, {
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error(
      `Remote fetch failed: ${response.status} ${response.statusText}`
    );
  }
  const content_data = await response.json();

  // Diagnostics
  try {
    const rawArr = Array.isArray(content_data)
      ? content_data
      : Array.isArray(content_data?.tables)
      ? content_data.tables
      : Array.isArray(content_data?.categories)
      ? content_data.categories
      : [];
    console.log("[tablesApi] Remote rows:", rawArr.length);
    console.log(
      "[tablesApi] Remote disabled (raw visibility==0):",
      rawArr.filter((t) => String(t?.visibility) === "0").length
    );
  } catch (ex) {
    console.log("[tablesApi] Debug parse error:", ex);
  }

  return content_data;
}

// ------------------------------
// Employees
// ------------------------------

/**
 * List employees for Waiter dropdown.
 * @param {string} tenantServerUrl tenant server URL from Auth
 * @param {{restaurantId?:string|number}} opts
 */
export async function listEmployees(tenantServerUrl, { restaurantId } = {}) {
  const url = fetchEmployeesEndpoint(tenantServerUrl);
  const data = await getJSON(url);

  const raw = Array.isArray(data) ? data : data?.employees || [];

  return raw.map((e) => ({
    id: e?.id ?? e?.employee_id ?? e?.user_id ?? e?.username ?? e?.email,
    username: e?.username ?? e?.email ?? e?.id,
    display_name: e?.display_name ?? e?.name ?? e?.fullname ?? e?.username,
    active: e?.active ?? 1,
  }));
}

// ------------------------------
// Normalize -> { tables: [...] }
// ------------------------------
function mapTablesToDB(tablesArray) {
  if (!Array.isArray(tablesArray)) {
    throw new Error("Unexpected payload format: expected an array");
  }

  const tables = tablesArray.map((t, i) => {
    const id = String(
      t.id ??
        t.table_id ??
        t.tableID ??
        t.number ??
        t.code ??
        (i + 1)
    );
    const name =
      t.name ??
      t.table_name ??
      t.tableName ??
      t.nickname ??
      t.label ??
      `Table ${i + 1}`;

    // Normalize visibility into '1' / '0' (default to '1' if missing)
    const vis =
      t.visibility === undefined || t.visibility === null
        ? "1"
        : boolTo10(toBool(t.visibility));

    // Keep every original field while guaranteeing id/name/visibility for UI lists
    return { id, name: String(name), visibility: vis, ...t };
  });

  // Diagnostics
  console.log(
    "[tablesApi] mapped:",
    tables.length,
    " total; disabled:",
    tables.filter((x) => x.visibility === "0").length
  );

  return { tables };
}

// ------------------------------
// Local fallback DB
// ------------------------------
let DB = {
  tables: [
    { id: "1", name: "Table 1", number: "1", status: "Vacant", visibility: "1" },
    {
      id: "2",
      name: "Table 2",
      number: "2",
      status: "Occupied",
      occupants: "2",
      visibility: "1",
    },
    {
      id: "3",
      name: "Table 3",
      number: "3",
      status: "Vacant",
      visibility: "0",
    }, // disabled example
    {
      id: "4",
      name: "Table 4",
      number: "4",
      status: "Cleaning",
      visibility: "1",
    },
  ],
};

// ------------------------------
// Public API (tenant-aware)
// ------------------------------

/**
 * Fetch tables (remote preferred, fallback to local).
 * @param {string} tenantServerUrl
 * @returns {Promise<{tables: Array}>}
 */
export async function fetchTables(tenantServerUrl) {
  try {
    DBG("fetchTables() start");
    const remote = await fetchRemoteTables(tenantServerUrl);
    const arr = Array.isArray(remote)
      ? remote
      : Array.isArray(remote?.tables)
      ? remote.tables
      : Array.isArray(remote?.categories)
      ? remote.categories
      : [];
    const mapped = mapTablesToDB(arr);
    DB = mapped;
    DBG("fetchTables() mapped count", DB.tables.length);
    return JSON.parse(JSON.stringify(DB));
  } catch (err) {
    console.warn("[tablesApi] fallback due to error:", err?.message || err);
    await delay();
    return JSON.parse(JSON.stringify(DB));
  }
}

/**
 * Force refresh from server.
 */
export async function refreshFromServer(tenantServerUrl) {
  DBG("refreshFromServer() start");
  const remote = await fetchRemoteTables(tenantServerUrl);
  const arr = Array.isArray(remote)
    ? remote
    : Array.isArray(remote?.tables)
    ? remote.tables
    : Array.isArray(remote?.categories)
    ? remote.categories
    : [];
  DB = mapTablesToDB(arr);
  DBG("refreshFromServer() mapped count", DB.tables.length);
  return JSON.parse(JSON.stringify(DB));
}

/**
 * Raw remote getter (diagnostics).
 */
export async function fetchRawRemote(tenantServerUrl) {
  return fetchRemoteTables(tenantServerUrl);
}

/**
 * Open a tab for a table.
 * @param {string} tenantServerUrl
 * @param {{ tableId, number, restaurantId, tabType, bywho }} params
 */
export function openTableTab(
  tenantServerUrl,
  { tableId, number, restaurantId, tabType, bywho }
) {
  const url = openTabEndpoint(tenantServerUrl);
  const payload = {
    table_id: tableId,
    number,
    restaurant_id: restaurantId,
    tabType,
    bywho,
  };
  DBG("openTableTab → payload", payload);
  return postWithRetry(url, payload);
}

/**
 * Close a tab.
 * @param {string} tenantServerUrl
 * @param {{ number, bywho }} params
 */
export function closeTableTab(tenantServerUrl, { number, bywho }) {
  const url = closeTabEndpoint(tenantServerUrl);
  const payload = { number, bywho };
  DBG("closeTableTab → payload", payload);
  return postWithRetry(url, payload);
}

/**
 * Force close a tab.
 * @param {string} tenantServerUrl
 * @param {{ number, bywho, waiter, total }} params
 */
export function forceCloseTable(
  tenantServerUrl,
  { number, bywho, waiter, total }
) {
  const url = forceCloseEndpoint(tenantServerUrl);
  const payload = { number, bywho, waiter, total };
  DBG("forceCloseTable → payload", payload);
  return postWithRetry(url, payload);
}

/**
 * Add a table via remote endpoint.
 * @param {string} tenantServerUrl
 * @param {object} payload
 */
export async function addTable(tenantServerUrl, payload) {
  const url = addTableEndpoint(tenantServerUrl);

  // normalize truthy flag: send both enabled & visibility for compatibility
  const enabled = (payload.enabled ?? payload.visibility ?? 1) ? 1 : 0;

  const resp = await postForm(url, {
    number:        payload.number,
    nickname:      payload.nickname,
    capacity:      payload.capacity ?? 0,
    waiter:        payload.default_waiter ?? payload.waiter ?? "",
    enabled,                      // new server supports this
    visibility:    enabled,       // legacy param
    restaurant_id: payload.restaurant_id ?? "1",
    bywho:
      payload.bywho ??
      localStorage.getItem("loggedUser_Username") ??
      localStorage.getItem("loggedUser_Email") ??
      "admin",
  });

  // new PHP returns { ok, message, created? }
  if (typeof resp === "object" && resp?.ok === false) {
    const err = new Error(resp.message || "Add table failed");
    err.payload = resp;
    throw err;
  }

  // If old server returns "success" string, fake a shape so callers can select newly created
  if (typeof resp === "string") {
    if (resp.trim().toLowerCase() === "exists") {
      const err = new Error("Table already exists");
      err.code = "exists";
      throw err;
    }
    if (resp.trim().toLowerCase() !== "success") {
      const err = new Error(`Unexpected response: ${resp}`);
      err.raw = resp;
      throw err;
    }
    // minimal echo
    return {
      id: payload.number,
      number: payload.number,
      nickname: payload.nickname,
      capacity: Number(payload.capacity || 0),
      visibility: enabled,
    };
  }

  // prefer structured response
  return resp.created ?? resp;
}

/**
 * Save table changes to the remote endpoint, then mirror into local DB.
 * @param {string} tenantServerUrl
 * @param {string|number} id
 * @param {string|number} number
 * @param {object} newData
 */
export async function editTable(tenantServerUrl, id, number, newData) {
  const url = editTableEndpoint(tenantServerUrl);
  const payload = { id, number, ...newData };

  if (payload.visibility === true) payload.visibility = "1";
  if (payload.visibility === false) payload.visibility = "0";

  DBG("editTable payload", payload);

  // try JSON first
  let resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });
  DBG("editTable JSON resp", resp.status, resp.statusText);

  // fallback to form
  if (!resp.ok) {
    const form = new URLSearchParams();
    Object.entries(payload).forEach(([k, v]) => form.append(k, v ?? ""));
    DBG("editTable FORM payload", Object.fromEntries(form));
    resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: form.toString(),
    });
    DBG("editTable FORM resp", resp.status, resp.statusText);
  }

  const body = await resp.text();
  DBG("editTable raw body", body?.slice(0, 500));
  if (!resp.ok)
    throw new Error(
      `Save failed: ${resp.status} ${resp.statusText} ${body || ""}`.trim()
    );

  let updated = null;
  try {
    updated = JSON.parse(body);
    DBG("editTable parsed JSON", updated);
  } catch {
    DBG("editTable non-JSON OK");
  }

  // mirror to local
  const t = DB.tables.find((x) => String(x.id) === String(id));
  if (!t) throw new Error("Table not found locally after save");
  Object.assign(
    t,
    updated && typeof updated === "object" ? updated : newData
  );
  if (t.visibility !== undefined) t.visibility = boolTo10(toBool(t.visibility));

  return t;
}

/**
 * Delete a table on the remote endpoint, then mirror removal into local DB.
 * @param {string} tenantServerUrl
 * @param {{ id?: string|number, number?: string|number, restaurantId?: string|number, bywho?: string }} params
 */
export async function deleteTable(
  tenantServerUrl,
  { id, number, restaurantId, bywho } = {}
) {
  const url = deleteTableEndpoint(tenantServerUrl);

  // Resolve identifiers
  const tableId = id ?? null;
  const tableNumber = number ?? null;

  // Fallback operator (mirrors addTable / resolveByWho style)
  let operator = bywho;
  if (!operator) {
    try {
      operator =
        localStorage.getItem("loggedUser_Username") ||
        localStorage.getItem("loggedUser_Email") ||
        "admin";
    } catch {
      operator = "admin";
    }
  }

  const payload = {
    id: tableId,
    number: tableNumber,
    restaurant_id: restaurantId ?? "1",
    bywho: operator,
  };
  console.log("Delete table "+tenantServerUrl + " - "+ JSON.stringify(payload))
  DBG("deleteTable payload", payload);
   DBG("deleteTable url", url);
 var resp = ""
  try{
     resp = await postForm(url, payload);
    console.log("[DELETE] Response: "+JSON.stringify(resp))
    // Handle simple string responses like "success"
    if (typeof resp === "string") {
      const txt = resp.trim().toLowerCase();
      if (txt !== "success") {
        const err = new Error(`Unexpected deleteTable response: ${resp}`);
        err.raw = resp;
        throw err;
      }
    } else if (typeof resp === "object" && resp !== null) {
      // If structured, honor { ok: false, message }
      if (resp.ok === false) {
        const err = new Error(resp.message || "Delete table failed");
        err.payload = resp;
        throw err;
      }
    
    }
  }catch(ex){
    console.log("[DELETE] Response: "+JSON.stringify(resp)  + " ex "+ex)
  }
  // Mirror removal to local DB cache
  DB.tables = DB.tables.filter((t) => {
    const sameId = tableId != null && String(t.id) === String(tableId);
    const sameNumber =
      tableNumber != null && String(t.number) === String(tableNumber);
    // Remove if either matches; otherwise keep
    return !(sameId || sameNumber);
  });

  return { ok: true };
}

/**
 * Import tables from JSON (accepts array or {tables:[...]}).
 */
export async function importTables(jsonText) {
  await delay();
  const parsed = JSON.parse(jsonText);

  const arr = Array.isArray(parsed?.tables)
    ? parsed.tables
    : Array.isArray(parsed)
    ? parsed
    : null;

  if (!arr) throw new Error("Invalid format: expected array or { tables: [...] }");

  DB = mapTablesToDB(arr);
  return fetchTables(); // note: this uses fallback DB only
}

/**
 * Export current DB (for backup/debug).
 */
export async function exportTables() {
  await delay();
  return JSON.stringify(DB, null, 2);
}

// Backwards-compat aliases – still require tenantServerUrl for the "real" calls.
// You can remove these if you’re not using them.
export const fetchMenu   = fetchTables;
export const addCategory = (tenantServerUrl, x) =>
  addTable(
    tenantServerUrl,
    typeof x === "string" ? { nickname: x } : x
  );
export const editCategory = (tenantServerUrl, id, newNameOrObj) => {
  if (typeof newNameOrObj === "string") {
    return editTable(tenantServerUrl, id, null, {
      name: newNameOrObj,
      nickname: newNameOrObj,
    });
  }
  return editTable(tenantServerUrl, id, null, newNameOrObj);
};
export const importMenu  = importTables;
export const exportMenu  = exportTables;
